import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportFullViewTableTitle, {
  useReportFullViewSection,
} from '@components/pages/Reports/shared/ReportFullViewTableTitle.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  CHANNEL_ROI_BAR_ROWS,
  CONVERSION_FUNNEL_STAGES,
  MARKETING_CAMPAIGN_FILTERS,
  MARKETING_CAMPAIGN_KPI_METRICS,
  MARKETING_CHANNEL_DETAIL_ROWS,
  MarketingChannelDetailRow,
  SPENT_VS_NEW_PATIENTS_MONTHLY,
} from '@constants/Reports/marketingCampaignPerformance.constants'
import { ReportsDistributionView } from '@dataTypes/reports.type'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 20, left: 4, bottom: 0 }
const REPORT_BAR_CHART_MARGIN_LEGEND = {
  top: 24,
  right: 20,
  left: 4,
  bottom: 12,
}
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 46

const CHANNEL_DETAIL_FULL_VIEW_COLUMNS: ReportDataTableColumn<MarketingChannelDetailRow>[] =
  [
    { key: 'ltv', label: 'LTV' },
    { key: 'ltvCac', label: 'LTV:CAC' },
    { key: 'leadToNew', label: 'Lead > New' },
  ]

function MarketingCampaignPerformanceReport(): React.ReactElement {
  const [spentMonthlyView, setSpentMonthlyView] =
    useState<ReportsDistributionView>('Count')
  const [channelRoiView, setChannelRoiView] =
    useState<ReportsDistributionView>('Count')
  const [funnelView, setFunnelView] = useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const channelDetailsFullView = useReportFullViewSection('mkt-channels')

  const spentVsPatientsChartData = useMemo(() => {
    if (spentMonthlyView === 'Count') {
      return SPENT_VS_NEW_PATIENTS_MONTHLY
    }
    const maxSpent = Math.max(
      ...SPENT_VS_NEW_PATIENTS_MONTHLY.map((r) => r.spentK),
      0.001,
    )
    const maxBounced = Math.max(
      ...SPENT_VS_NEW_PATIENTS_MONTHLY.map((r) => r.bounced),
      1,
    )
    const maxUnsub = Math.max(
      ...SPENT_VS_NEW_PATIENTS_MONTHLY.map((r) => r.unsubscribed),
      1,
    )
    return SPENT_VS_NEW_PATIENTS_MONTHLY.map((row) => ({
      ...row,
      spentK: Math.round((row.spentK / maxSpent) * 1000) / 10,
      bounced: Math.round((row.bounced / maxBounced) * 1000) / 10,
      unsubscribed: Math.round((row.unsubscribed / maxUnsub) * 1000) / 10,
    }))
  }, [spentMonthlyView])

  const channelRoiRoiTotal = useMemo(
    () => CHANNEL_ROI_BAR_ROWS.reduce((sum, r) => sum + r.roi, 0) || 1,
    [],
  )

  const channelRoiChartData = useMemo(
    () =>
      CHANNEL_ROI_BAR_ROWS.map((row) => {
        if (channelRoiView === 'Count') {
          return {
            ...row,
            barValue: row.roi,
            topLabel: String(row.roi),
          }
        }
        const pct = Math.round((row.roi / channelRoiRoiTotal) * 1000) / 10
        return { ...row, barValue: pct, topLabel: `${pct}%` }
      }),
    [channelRoiView, channelRoiRoiTotal],
  )

  const channelRoiYAxis = useMemo(() => {
    if (channelRoiView === 'Count') {
      return {
        domain: [0, 70] as [number, number],
        ticks: [0, 10, 20, 30, 40, 50, 60, 70],
        tickFormatter: (v: number) => String(v),
      }
    }
    return {
      domain: [0, 100] as [number, number],
      ticks: [0, 25, 50, 75, 100],
      tickFormatter: (v: number) => `${v}%`,
    }
  }, [channelRoiView])

  const funnelFirstCount = CONVERSION_FUNNEL_STAGES[0]?.count ?? 1

  const funnelChartData = useMemo(
    () =>
      CONVERSION_FUNNEL_STAGES.map((row) => {
        const pctCount = Math.round((row.count / funnelFirstCount) * 1000) / 10
        const barValue = funnelView === 'Count' ? row.count : pctCount
        const topLabel =
          funnelView === 'Count' ? String(row.count) : `${pctCount}%`
        return {
          ...row,
          label: row.label,
          barValue,
          topLabel,
        }
      }),
    [funnelView, funnelFirstCount],
  )

  const funnelYAxis = useMemo(() => {
    if (funnelView === 'Count') {
      // Domain max must match the top tick or Recharts leaves a blank band at the top and the
      // upper grid line reads as an extra line above the plot.
      return {
        domain: [0, 500] as [number, number],
        ticks: [0, 100, 200, 300, 400, 500],
        tickFormatter: (v: number) => String(v),
      }
    }
    return {
      domain: [0, 100] as [number, number],
      ticks: [0, 25, 50, 75, 100],
      tickFormatter: (v: number) => `${v}%`,
    }
  }, [funnelView])

  const channelDetailColumns: ReportDataTableColumn<MarketingChannelDetailRow>[] =
    useMemo(
      () => [
        { key: 'channel', label: 'Channel' },
        { key: 'spend', label: 'Spend' },
        { key: 'newPatients', label: 'New Patients' },
        { key: 'cpa', label: 'CPA' },
        ...(channelDetailsFullView.isFullView
          ? CHANNEL_DETAIL_FULL_VIEW_COLUMNS
          : []),
      ],
      [channelDetailsFullView.isFullView],
    )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-marketing-campaign-performance">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Marketing Campaign Performance">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={MARKETING_CAMPAIGN_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <div className="reports-treatment-conversion-funnel__stack">
        <ReportExpandableSection sectionId="mkt-upper">
          <ReportKpiGrid items={MARKETING_CAMPAIGN_KPI_METRICS} />

          <div className="reports-treatment-conversion-funnel__grid">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Spent vs New Patients (monthly)
                  </ReportSectionHeading>
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={spentMonthlyView}
                    onChange={(value) =>
                      setSpentMonthlyView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                </div>
                <p className="reports-treatment-conversion-funnel__chart-note">
                  Delivered (spend $K), bounced, and unsubscribed volumes by
                  month
                </p>
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={spentVsPatientsChartData}
                      margin={REPORT_BAR_CHART_MARGIN_LEGEND}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        yAxisId="spend"
                        domain={
                          spentMonthlyView === 'Count' ? [0, 6] : [0, 100]
                        }
                        ticks={
                          spentMonthlyView === 'Count'
                            ? [0, 1, 2, 3, 4, 5, 6]
                            : [0, 25, 50, 75, 100]
                        }
                        tickFormatter={(v) =>
                          spentMonthlyView === 'Count' ? `$${v}K` : `${v}%`
                        }
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                        label={{
                          value:
                            spentMonthlyView === 'Count'
                              ? 'Spend ($K)'
                              : '% of peak (spend)',
                          angle: -90,
                          position: 'insideLeft',
                          fill: 'var(--gray-500)',
                          fontSize: 11,
                        }}
                      />
                      <YAxis
                        yAxisId="count"
                        orientation="right"
                        domain={
                          spentMonthlyView === 'Count' ? [0, 35] : [0, 100]
                        }
                        ticks={
                          spentMonthlyView === 'Count'
                            ? [0, 5, 10, 15, 20, 25, 30, 35]
                            : [0, 25, 50, 75, 100]
                        }
                        tickFormatter={(v) =>
                          spentMonthlyView === 'Count' ? String(v) : `${v}%`
                        }
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                        label={{
                          value:
                            spentMonthlyView === 'Count'
                              ? 'Count'
                              : '% of peak (volume)',
                          angle: 90,
                          position: 'insideRight',
                          fill: 'var(--gray-500)',
                          fontSize: 11,
                        }}
                      />
                      <Tooltip
                        formatter={(
                          value: number,
                          name: string,
                        ): [string, string] => {
                          if (spentMonthlyView === 'Percentage') {
                            return [`${value}%`, name]
                          }
                          if (name.includes('spent') || name === 'spentK')
                            return [`$${value}K`, 'Delivered (spend)']
                          return [String(value), name]
                        }}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Legend
                        wrapperStyle={{
                          fontSize: 12,
                          paddingTop: 0,
                          lineHeight: '16px',
                        }}
                        iconType="circle"
                        iconSize={8}
                      />
                      <Bar
                        yAxisId="spend"
                        dataKey="spentK"
                        name="Delivered (spend)"
                        fill="var(--blue-600)"
                        maxBarSize={36}
                        radius={[8, 8, 8, 8]}
                      />
                      <Bar
                        yAxisId="count"
                        dataKey="bounced"
                        name="Bounced"
                        fill="var(--blue-400)"
                        maxBarSize={36}
                        radius={[8, 8, 8, 8]}
                      />
                      <Bar
                        yAxisId="count"
                        dataKey="unsubscribed"
                        name="Unsubscribed"
                        fill="var(--orange-300)"
                        maxBarSize={36}
                        radius={[8, 8, 8, 8]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ReportSectionCard>

            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Channel Performance (ROI)
                  </ReportSectionHeading>
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={channelRoiView}
                    onChange={(value) =>
                      setChannelRoiView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                </div>
                <p className="reports-treatment-conversion-funnel__chart-note">
                  ROI by channel (tracked revenue)
                </p>
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={channelRoiChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={channelRoiYAxis.domain}
                        ticks={channelRoiYAxis.ticks}
                        tickFormatter={channelRoiYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          channelRoiView === 'Count'
                            ? [value, 'ROI index']
                            : [`${value}%`, 'Share']
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        barSize={100}
                        radius={[8, 8, 8, 8]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ReportSectionCard>
          </div>
        </ReportExpandableSection>

        <div className="reports-treatment-conversion-funnel__grid">
          <ReportExpandableSection sectionId="mkt-funnel">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Conversion Funnel (All Channels)
                  </ReportSectionHeading>
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={funnelView}
                    onChange={(value) =>
                      setFunnelView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                </div>
                <p className="reports-treatment-conversion-funnel__chart-note">
                  Leads → Appointments → New Patients
                </p>
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={funnelChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={funnelYAxis.domain}
                        ticks={funnelYAxis.ticks}
                        tickFormatter={funnelYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          funnelView === 'Count'
                            ? [value, 'Volume']
                            : [`${value}%`, 'Of leads']
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        barSize={100}
                        radius={[8, 8, 8, 8]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="mkt-channels">
            <ReportSectionCard className="reports-patient-base-health__chart-card reports-treatment-conversion-funnel__table-card">
              <div className="reports-patient-base-health__chart-head">
                <ReportFullViewTableTitle
                  sectionId="mkt-channels"
                  title="Channel Details"
                />
              </div>
              <p className="reports-treatment-conversion-funnel__chart-note">
                CPA, LTV:CAC, conversion rates
              </p>
              <ReportDataTable
                key={
                  channelDetailsFullView.isFullView
                    ? 'mkt-channels-fv'
                    : 'mkt-channels-card'
                }
                columns={channelDetailColumns}
                rows={MARKETING_CHANNEL_DETAIL_ROWS}
                rowKey={(row) => row.id}
                initialPageSize={5}
                className={[
                  'reports-marketing-campaign-performance__detail-table',
                  channelDetailsFullView.isFullView
                    ? 'reports-patient-value-revenue__provider-table--full-view'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                fullViewSectionId="mkt-channels"
                fullViewSearchWithFilter
              />
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default MarketingCampaignPerformanceReport
